// @ts-nocheck
// Prevent multiple declarations if script is loaded more than once
if (typeof window.StoreSearch === 'undefined') {
  /**
   * Store Search Component
   * 
   * Extends the autocomplete pattern to search for physical store locations.
   * Supports multiple geocoding services (Google Maps or Mapbox) to convert 
   * addresses to coordinates, then queries Shopify Storefront API for nearby 
   * store locations.
   * 
   * @extends HTMLElement
   * 
   * @attr {string} geocoding-service - Geocoding service to use: "google" (default) or "mapbox"
   * @attr {string} name - Name for hidden input field containing selected location IDs
   * @attr {string} country-field-selector - CSS selector for country select field
   * @attr {string} region-restriction - Optional region/province restriction for geocoding
   * @attr {string} language - Language code for Mapbox results (default: "en")
   * @attr {number} debounce - Debounce delay in ms (default: 300)
   * @attr {number} limit - Maximum number of locations to return (default: 5)
   * @attr {boolean} required - Whether selection is required for form submission
   * @attr {string} validation-message - Custom validation message when required field is empty
   * 
   * @fires store:select - Fired when a location is selected
   * @fires store:deselect - Fired when a location is deselected
   * @fires store:remove - Fired when a location chip is removed
   * @fires store:clear - Fired when all selections are cleared
   * 
   * @example
   * <!-- Using Google Maps (default) -->
   * <store-search 
   *   name="store_locations"
   *   country-field-selector="#country-select"
   *   required
   *   validation-message="Please select your preferred bakery">
   *   <input type="search" placeholder="Enter address...">
   * </store-search>
   * 
   * @example
   * <!-- Using Mapbox -->
   * <store-search 
   *   geocoding-service="mapbox"
   *   name="store_locations"
   *   country-field-selector="#country-select"
   *   language="en"
   *   limit="10">
   *   <input type="search" placeholder="Enter address...">
   * </store-search>
   */
  window.StoreSearch = class StoreSearch extends HTMLElement {
    constructor() {
      super();
      /** @type {HTMLInputElement|null} */
      this.input = null;
      /** @type {HTMLSelectElement|null} */
      this.countrySelect = null;
      /** @type {HTMLElement|null} */
      this.resultsPanel = null;
      /** @type {HTMLInputElement|null} */
      this.hiddenInput = null;
      /** @type {HTMLElement|null} */
      this.chipsContainer = null;
      /** @type {HTMLElement|null} */
      this.validationMessage = null;
      /** @type {HTMLFormElement|null} */
      this.form = null;
      /** @type {Function|null} */
      this.formSubmitHandler = null;
      /** @type {HTMLButtonElement|null} */
      this.clearInputButton = null;
      /** @type {HTMLElement|null} */
      this.searchIcon = null;
      /** @type {HTMLElement|null} */
      this.loadingIndicator = null;
      /** @type {AbortController|null} */
      this.abortController = null;
      /** @type {number|null} */
      this.debounceTimer = null;
      /** @type {number} */
      this.activeIndex = -1;
      /** @type {Array<Object>} */
      this.locations = [];
      /** @type {Array<Object>} */
      this.selectedLocations = [];
      /** @type {boolean} */
      this.isLoading = false;
    }
    connectedCallback() {
      this.#initialize();
    }
    disconnectedCallback() {
      this.#cleanup();
    }
    /**
     * Initialize component
     */
    #initialize() {
      this.input = this.querySelector('input[type="search"], input[type="text"]');
      // Look for country selector using attribute value as selector
      // First try in closest form, then fallback to document
      const countrySelector = this.getAttribute('country-field-selector');
      if (countrySelector) {
        const closestForm = this.closest('form');
        this.countrySelect = closestForm
          ? closestForm.querySelector(countrySelector)
          : document.querySelector(countrySelector);
      }
      if (!this.input) {
        console.error('StoreSearch: No input element found');
        return;
      }
      this.#createStructure();
      this.#attachEventListeners();
    }
    /**
     * Create component structure
     */
    #createStructure() {
      if (!this.input) return;
      // Check if multiple selection is enabled
      this.allowMultiple = this.hasAttribute('multiple');
      // Wrap input in container
      const inputWrapper = document.createElement('div');
      inputWrapper.className = 'store-search__input-wrapper';
      // Move input into wrapper
      this.input.parentNode?.insertBefore(inputWrapper, this.input);
      inputWrapper.appendChild(this.input);
      // Create clear input button
      this.clearInputButton = document.createElement('button');
      this.clearInputButton.type = 'button';
      this.clearInputButton.className = 'store-search__clear-input';
      this.clearInputButton.setAttribute('aria-label', 'Clear search');
      this.clearInputButton.hidden = true;
      this.clearInputButton.innerHTML = `
      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
        <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"/>
      </svg>
    `;
      inputWrapper.appendChild(this.clearInputButton);
      // Create separator
      const separator = document.createElement('div');
      separator.className = 'store-search__separator';
      inputWrapper.appendChild(separator);
      // Create icon container
      const iconContainer = document.createElement('div');
      iconContainer.className = 'store-search__icon-container';
      // Create search icon
      this.searchIcon = document.createElement('div');
      this.searchIcon.className = 'store-search__search-icon';
      this.searchIcon.innerHTML = `
      <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
        <path d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"/>
      </svg>
    `;
      iconContainer.appendChild(this.searchIcon);
      // Create loading indicator
      this.loadingIndicator = document.createElement('div');
      this.loadingIndicator.className = 'store-search__loading';
      this.loadingIndicator.hidden = true;
      this.loadingIndicator.innerHTML = `
      <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
        <path d="M10 2a8 8 0 100 16 8 8 0 000-16zm0 14a6 6 0 110-12 6 6 0 010 12z" opacity="0.3"/>
        <path d="M10 2a8 8 0 018 8h-2a6 6 0 00-6-6V2z"/>
      </svg>
    `;
      iconContainer.appendChild(this.loadingIndicator);
      inputWrapper.appendChild(iconContainer);
      // Create results panel
      this.resultsPanel = document.createElement('div');
      this.resultsPanel.className = 'store-search__results';
      this.resultsPanel.setAttribute('role', 'listbox');
      this.resultsPanel.setAttribute('aria-multiselectable', String(this.allowMultiple));
      this.resultsPanel.hidden = true;
      this.appendChild(this.resultsPanel);
      // Create hidden input for form submission
      const name = this.getAttribute('name');
      if (name) {
        this.hiddenInput = document.createElement('input');
        this.hiddenInput.type = 'hidden';
        this.hiddenInput.name = name;
        // Note: We handle validation through custom form submit handler instead of
        // using built-in HTML5 validation because hidden inputs don't show validation messages properly
        if (this.hasAttribute('required')) {
          this.hiddenInput.required = true;
        }
        this.appendChild(this.hiddenInput);
        // Remove name from visible input to avoid conflicts
        this.input?.removeAttribute('name');
      }
      // Create chips container
      this.chipsContainer = document.createElement('div');
      this.chipsContainer.className = 'store-search__chips';
      this.chipsContainer.hidden = true;
      // Create validation message container
      this.validationMessage = document.createElement('div');
      this.validationMessage.className = 'store-search__validation-message';
      this.validationMessage.setAttribute('role', 'alert');
      this.validationMessage.setAttribute('aria-live', 'polite');
      this.validationMessage.textContent = ''; // Ensure empty initially
      this.validationMessage.hidden = true;
      this.validationMessage.style.display = 'none'; // Extra safety
      this.appendChild(this.chipsContainer);
      this.appendChild(this.validationMessage);
    }
    /**
     * Attach event listeners
     */
    #attachEventListeners() {
      if (!this.input || !this.resultsPanel) return;
      const debounce = parseInt(this.getAttribute('debounce') || '300');
      this.input.addEventListener('input', (event) => {
        const target = event.target;
        if (!(target instanceof HTMLInputElement)) return;
        // Show/hide clear button
        if (this.clearInputButton) {
          this.clearInputButton.hidden = target.value.length === 0;
        }
        if (this.debounceTimer) {
          clearTimeout(this.debounceTimer);
        }
        // If input is empty or too short, immediately stop loading and close results
        if (target.value.trim().length < 2) {
          this.#setLoading(false);
          this.#closeResults();
          return;
        }
        this.#setLoading(true);
        this.debounceTimer = setTimeout(() => {
          this.#handleInput(target.value);
        }, debounce);
      });
      this.input.addEventListener('keydown', (event) => {
        this.#handleKeyboard(event);
      });
      this.input.addEventListener('focus', async () => {
        // Show results panel if there are locations to display
        if (this.locations.length > 0 && this.resultsPanel) {
          this.resultsPanel.hidden = false;
        }
        // Trigger search on focus if input has value
        if (this.input.value.trim().length >= 2) {
          await this.#handleInput(this.input.value);
        }
      });
      this.input.addEventListener('blur', (event) => {
        setTimeout(() => {
          // Don't close if clicking inside the results panel (for multiple selection)
          if (this.allowMultiple && this.resultsPanel?.contains(document.activeElement)) {
            return;
          }
          this.#closeResults();
        }, 200);
      });
      // Clear input button
      this.clearInputButton?.addEventListener('click', () => {
        if (this.input) {
          this.input.value = '';
          // trigger input event change event
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
          this.input.focus();
        }
        if (this.clearInputButton) {
          this.clearInputButton.hidden = true;
        }
        // Clear locations data to prevent showing old results on focus
        this.locations = [];
        this.activeIndex = -1;
        this.#setLoading(false);
        this.#closeResults();
      });
      this.resultsPanel.addEventListener('change', (event) => {
        const target = event.target;
        if (!(target instanceof HTMLInputElement) || (target.type !== 'checkbox' && target.type !== 'radio')) return;
        const option = target.closest('[role="option"]');
        if (option instanceof HTMLElement) {
          this.#toggleLocation(option, target.checked);
        }
      });
      // Prevent panel from closing when clicking inside (for multiple selection)
      this.resultsPanel.addEventListener('mousedown', (event) => {
        if (this.allowMultiple) {
          event.preventDefault(); // Prevent input from losing focus
        }
      });
      // Close panel when clicking outside (for multiple selection)
      document.addEventListener('click', (event) => {
        if (!this.allowMultiple) return; // Only for multiple selection mode
        const target = event.target;
        // Check if click is outside both input and results panel
        if (target instanceof Node &&
          !this.input?.contains(target) &&
          !this.resultsPanel?.contains(target) &&
          !this.resultsPanel?.hidden) {
          this.#closeResults();
        }
      });
      // Setup form validation if required
      if (this.hasAttribute('required')) {
        this.#setupFormValidation();
      }
    }
    /**
     * Handle input changes
     * @param {string} value - Input value
     */
    async #handleInput(value) {
      const trimmedValue = value.trim();
      if (trimmedValue.length < 2) {
        this.#closeResults();
        this.#setLoading(false);
        return;
      }
      await this.#searchLocations(trimmedValue);
    }
    /**
     * Get province value from form field
     * @returns {string|null}
     */
    #getProvinceValue() {
      const provinceFieldSelector = this.getAttribute('data-field-province');
      if (!provinceFieldSelector) return null;
      // Look for province field in closest form, then fallback to document
      const closestForm = this.closest('form');
      const provinceField = closestForm
        ? closestForm.querySelector(provinceFieldSelector + ' input, ' + provinceFieldSelector + ' select')
        : document.querySelector(provinceFieldSelector + ' input, ' + provinceFieldSelector + ' select');
      if (!provinceField) return null;
      return provinceField.value?.trim() || null;
    }
    /**
     * Search locations by bakery name using Shopify Storefront API with pagination
     * Searches by public_store_name metafield (namespace: "amb", key: "public_store_name")
     * Fetches ALL locations using pagination to ensure complete coverage (~700 locations)
     * @param {string} query - Search query (bakery name)
     * @returns {Promise<Array<Object>|null>}
     */
    async #searchLocationsByName(query) {
      const settings = window.Shopify?.theme?.settings;
      const accessToken = settings ? settings.storefront_access_token : undefined;
      const apiVersion = settings?.storefront_api_version || '2025-10';
      if (!accessToken) {
        console.error('StoreSearch: Storefront API access token not configured');
        return null;
      }
      // GraphQL query with pagination support (cursor-based)
      const graphqlQuery = `
        query GetAllLocations($first: Int!, $after: String) {
          locations(first: $first, after: $after) {
            pageInfo {
              hasNextPage
              endCursor
            }
            edges {
              node {
                id
                name
                address {
                  address1
                  address2
                  city
                  provinceCode
                  zip
                  countryCode
                }
                metafield(namespace: "amb", key: "public_store_name") {
                  value
                }
                emailMetafield: metafield(namespace: "amb", key: "email") {
                  value
                }
              }
            }
          }
        }
      `;
      // TEMPORARY FIX: Use actual Shopify store domain when running locally
      const hostname = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
        ? 'bakers-delight-live.myshopify.com'
        : window.location.hostname;
      try {
        let allLocations = [];
        let hasNextPage = true;
        let cursor = null;
        // Fetch all pages (up to 700+ locations)
        while (hasNextPage) {
          const response = await fetch(`https://${hostname}/api/${apiVersion}/graphql.json`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Storefront-Access-Token': accessToken
            },
            body: JSON.stringify({
              query: graphqlQuery,
              variables: {
                first: 250, // Max per page
                after: cursor
              }
            }),
            signal: this.abortController?.signal
          });
          if (!response.ok) {
            throw new Error(`Storefront API failed: ${response.status}`);
          }
          const data = await response.json();
          if (data.errors) {
            console.error('StoreSearch: GraphQL errors', data.errors);
            return null;
          }
          // Extract locations from this page
          const pageLocations = data.data?.locations?.edges?.map((edge) => {
            const node = edge.node;
            const publicStoreName = node.metafield?.value || node.name;
            const email = node.emailMetafield?.value || '';
            return {
              ...node,
              name: publicStoreName,
              email
            };
          }) || [];
          allLocations = allLocations.concat(pageLocations);
          // Check if there are more pages
          const pageInfo = data.data?.locations?.pageInfo;
          hasNextPage = pageInfo?.hasNextPage || false;
          cursor = pageInfo?.endCursor || null;
        }
        console.log(`StoreSearch: Fetched ${allLocations.length} total locations`);
        // Filter locations by matching query against public_store_name (case-insensitive)
        const queryLower = query.toLowerCase().trim();
        const matchedLocations = allLocations.filter(location => {
          return location.name.toLowerCase().includes(queryLower);
        });
        console.log(`StoreSearch: Found ${matchedLocations.length} locations matching "${query}"`);
        return matchedLocations.length > 0 ? matchedLocations : null;
      } catch (error) {
        if (error.name === 'AbortError') throw error;
        console.error('StoreSearch: Name search error', error);
        return null;
      }
    }
    /**
     * Search for locations with province filtering and pagination
     * @param {string} query - Search query
     */
    async #searchLocations(query) {
      // Cancel previous request
      this.abortController?.abort();
      this.abortController = new AbortController();
      try {
        this.#setLoading(true);
        // Get selected country
        const country = this.countrySelect?.value || this.getAttribute('country-value') || 'AU';
        // Step 1: Try searching by bakery name first
        const nameResults = await this.#searchLocationsByName(query);
        if (nameResults && nameResults.length > 0) {
          // Found results by name, use them
          const limit = parseInt(this.getAttribute('limit') || '5');
          const finalLocations = nameResults.slice(0, limit);
          this.locations = finalLocations;
          this.#renderResults(finalLocations);
          this.#setLoading(false);
          return;
        }
        // Step 2: If no name results, fall back to geocoding address search
        const coordinates = await this.#geocodeAddress(query, country);
        if (!coordinates) {
          this.#showEmptyState('No locations found');
          this.#setLoading(false);
          return;
        }
        // Step 2: Get province code for filtering (separate API call)
        let targetProvinceCode = null;
        const provinceFieldValue = this.#getProvinceValue();
        if (provinceFieldValue) {
          // Make separate API call to get province code
          const provinceResult = await this.#getProvinceCode(provinceFieldValue, country);
          if (provinceResult) {
            targetProvinceCode = provinceResult;
          }
        }
        // Step 3: Query Shopify locations (get 100 results)
        const result = await this.#queryShopifyLocations(
          coordinates.lat,
          coordinates.lng
        );
        if (!result.locations || result.locations.length === 0) {
          this.#showEmptyState('No stores found near this location');
          this.#setLoading(false);
          return;
        }
        // Filter by province code if available
        let filteredLocations = result.locations;
        if (targetProvinceCode) {
          filteredLocations = result.locations.filter(location =>
            location.address?.provinceCode === targetProvinceCode
          );
        }
        // Limit to requested number of results
        const limit = parseInt(this.getAttribute('limit') || '5');
        const finalLocations = filteredLocations.slice(0, limit);
        if (finalLocations.length === 0) {
          const message = targetProvinceCode
            ? `No stores found in ${provinceFieldValue} near this location`
            : 'No stores found near this location';
          this.#showEmptyState(message);
          this.#setLoading(false);
          return;
        }
        this.locations = finalLocations;
        this.#renderResults(finalLocations);
        this.#setLoading(false);
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('StoreSearch: Search error', error);
        this.#showEmptyState('An error occurred. Please try again.');
        this.#setLoading(false);
      }
    }
    /**
     * Set loading state
     * @param {boolean} loading
     */
    #setLoading(loading) {
      this.isLoading = loading;
      if (this.searchIcon) {
        this.searchIcon.hidden = loading;
      }
      if (this.loadingIndicator) {
        this.loadingIndicator.hidden = !loading;
      }
    }
    /**
     * Geocode address using configured service (Google Maps or Mapbox)
     * @param {string} address - Address to geocode
     * @param {string} country - Country code (ISO 3166-1 alpha-2)
     * @returns {Promise<{lat: number, lng: number}|null>}
     */
    async #geocodeAddress(address, country) {
      const settings = window.Shopify?.theme?.settings;
      const mapProvider = settings?.map_provider || 'mapbox';
      if (mapProvider === 'mapbox') {
        return this.#geocodeWithMapbox(address, country);
      }
      return this.#geocodeWithGoogle(address, country);
    }
    /**
     * Get province code using configured service (Google Maps or Mapbox)
     * @param {string} address - Address to geocode
     * @param {string} country - Country code (ISO 3166-1 alpha-2)
     * @returns {Promise<string|null>}
     */
    async #getProvinceCode(address, country) {
      const settings = window.Shopify?.theme?.settings;
      const mapProvider = settings?.map_provider || 'mapbox';
      if (mapProvider === 'mapbox') {
        return this.#getProvinceCodeWithMapbox(address, country);
      }
      return this.#getProvinceCodeWithGoogle(address, country);
    }
    /**
     * Geocode address using Google Maps Geocoding API
     * @param {string} address - Address to geocode
     * @param {string} country - Country code (ISO 3166-1 alpha-2)
     * @returns {Promise<{lat: number, lng: number}|null>}
     */
    async #geocodeWithGoogle(address, country) {
      const settings = window.Shopify?.theme?.settings;
      const apiKey = settings ? settings.map_access_token : undefined;
      if (!apiKey) {
        console.error('StoreSearch: Map access token not configured');
        return null;
      }
      const url = new URL('https://maps.googleapis.com/maps/api/geocode/json');
      url.searchParams.set('address', address);
      url.searchParams.set('components', `country:${country}`);
      url.searchParams.set('key', apiKey);
      try {
        const response = await fetch(url.toString(), {
          signal: this.abortController?.signal
        });
        if (!response.ok) {
          throw new Error(`Google Geocoding failed: ${response.status}`);
        }
        const data = await response.json();
        if (data.status !== 'OK' || !data.results?.[0]) {
          console.warn('StoreSearch: Google geocoding returned no results', data.status);
          return null;
        }
        const location = data.results[0].geometry.location;
        return {
          lat: location.lat,
          lng: location.lng
        };
      } catch (error) {
        if (error.name === 'AbortError') throw error;
        console.error('StoreSearch: Google geocoding error', error);
        return null;
      }
    }
    /**
     * Geocode address using Mapbox Geocoding API v6
     * @param {string} address - Address to geocode
     * @param {string} country - Country code (ISO 3166-1 alpha-2)
     * @returns {Promise<{lat: number, lng: number}|null>}
     */
    async #geocodeWithMapbox(address, country) {
      const settings = window.Shopify?.theme?.settings;
      const apiKey = settings ? settings.map_access_token : undefined;
      if (!apiKey) {
        console.error('StoreSearch: Map access token not configured');
        return null;
      }
      // Build Mapbox Geocoding API v6 URL
      const url = new URL('https://api.mapbox.com/search/geocode/v6/forward');
      // Required parameters
      url.searchParams.set('q', address);
      url.searchParams.set('access_token', apiKey);
      // Optional parameters for better results
      url.searchParams.set('country', country.toLowerCase());
      url.searchParams.set('autocomplete', 'false'); // Disable autocomplete for exact matches
      url.searchParams.set('limit', '1'); // Only need the best result
      // Get optional region restriction from attribute
      const regionRestriction = this.getAttribute('region-restriction') || true;
      if (regionRestriction) {
        // Mapbox uses types parameter to filter by feature types
        // Get search types from data-search-types attribute with fallback
        const searchTypes = this.getAttribute('data-search-types') || 'address,place,locality,postcode';
        url.searchParams.set('types', searchTypes);
      }
      // Optional: Set language preference
      const language = this.getAttribute('language') || 'en';
      url.searchParams.set('language', language);
      try {
        const response = await fetch(url.toString(), {
          signal: this.abortController?.signal
        });
        if (!response.ok) {
          throw new Error(`Mapbox Geocoding failed: ${response.status}`);
        }
        const data = await response.json();
        // Check if we have valid results
        if (!data.features || data.features.length === 0) {
          console.warn('StoreSearch: Mapbox geocoding returned no results');
          return null;
        }
        // Get coordinates from first result
        const feature = data.features[0];
        const coordinates = feature.geometry.coordinates;
        // Mapbox returns [longitude, latitude] format
        return {
          lat: coordinates[1],
          lng: coordinates[0]
        };
      } catch (error) {
        if (error.name === 'AbortError') throw error;
        console.error('StoreSearch: Mapbox geocoding error', error);
        return null;
      }
    }
    /**
     * Get province code using Google Maps Geocoding API
     * @param {string} address - Address to geocode
     * @param {string} country - Country code (ISO 3166-1 alpha-2)
     * @returns {Promise<string|null>}
     */
    async #getProvinceCodeWithGoogle(address, country) {
      const settings = window.Shopify?.theme?.settings;
      const apiKey = settings ? settings.map_access_token : undefined;
      if (!apiKey) {
        console.error('StoreSearch: Map access token not configured');
        return null;
      }
      const url = new URL('https://maps.googleapis.com/maps/api/geocode/json');
      url.searchParams.set('address', address);
      url.searchParams.set('components', `country:${country}`);
      url.searchParams.set('key', apiKey);
      try {
        const response = await fetch(url.toString(), {
          signal: this.abortController?.signal
        });
        if (!response.ok) {
          throw new Error(`Google Geocoding failed: ${response.status}`);
        }
        const data = await response.json();
        if (data.status !== 'OK' || !data.results?.[0]) {
          console.warn('StoreSearch: Google geocoding returned no results', data.status);
          return null;
        }
        const result = data.results[0];
        // Extract province code from address components
        if (result.address_components) {
          const provinceComponent = result.address_components.find(component =>
            component.types.includes('administrative_area_level_1')
          );
          if (provinceComponent) {
            return provinceComponent.short_name;
          }
        }
        return null;
      } catch (error) {
        if (error.name === 'AbortError') throw error;
        console.error('StoreSearch: Google geocoding error', error);
        return null;
      }
    }
    /**
     * Get province code using Mapbox Geocoding API v6
     * @param {string} address - Address to geocode
     * @param {string} country - Country code (ISO 3166-1 alpha-2)
     * @returns {Promise<string|null>}
     */
    async #getProvinceCodeWithMapbox(address, country) {
      const settings = window.Shopify?.theme?.settings;
      const apiKey = settings ? settings.map_access_token : undefined;
      if (!apiKey) {
        console.error('StoreSearch: Map access token not configured');
        return null;
      }
      // Build Mapbox Geocoding API v6 URL
      const url = new URL('https://api.mapbox.com/search/geocode/v6/forward');
      // Required parameters
      url.searchParams.set('q', address);
      url.searchParams.set('access_token', apiKey);
      // Optional parameters for better results
      url.searchParams.set('country', country.toLowerCase());
      url.searchParams.set('autocomplete', 'false'); // Disable autocomplete for exact matches
      url.searchParams.set('limit', '1'); // Only need the best result
      // Get optional region restriction from attribute
      const regionRestriction = this.getAttribute('region-restriction') || true;
      if (regionRestriction) {
        // Mapbox uses types parameter to filter by feature types
        // Get search types from data-search-types attribute with fallback
        const searchTypes = 'region';
        url.searchParams.set('types', searchTypes);
      }
      // Optional: Set language preference
      const language = this.getAttribute('language') || 'en';
      url.searchParams.set('language', language);
      try {
        const response = await fetch(url.toString(), {
          signal: this.abortController?.signal
        });
        if (!response.ok) {
          throw new Error(`Mapbox Geocoding failed: ${response.status}`);
        }
        const data = await response.json();
        // Check if we have valid results
        if (!data.features || data.features.length === 0) {
          console.warn('StoreSearch: Mapbox geocoding returned no results');
          return null;
        }
        // Get province code from first result
        const feature = data.features[0];
        // Extract province code from context
        if (feature.properties?.context) {
          const context = feature.properties.context;
          // Check for region (province/state) in context object
          if (context.region) {
            return context.region.region_code || context.region.name;
          }
          // Fallback: check for other possible keys
          if (context.province) {
            return context.province.region_code || context.province.name;
          }
          if (context.state) {
            return context.state.region_code || context.state.name;
          }
        }
        return null;
      } catch (error) {
        if (error.name === 'AbortError') throw error;
        console.error('StoreSearch: Mapbox geocoding error', error);
        return null;
      }
    }
    /**
     * Query Shopify locations using Storefront API
     * @param {number} latitude - Latitude
     * @param {number} longitude - Longitude
     * @returns {Promise<{locations: Array<Object>}>}
     */
    async #queryShopifyLocations(latitude, longitude) {
      const settings = window.Shopify?.theme?.settings;
      const accessToken = settings ? settings.storefront_access_token : undefined;
      const apiVersion = settings?.storefront_api_version || '2025-10';
      if (!accessToken) {
        console.error('StoreSearch: Storefront API access token not configured');
        return { locations: [] };
      }
      const query = `
      query GetLocations($latitude: Float!, $longitude: Float!, $first: Int!) {
        locations(
          near: { latitude: $latitude, longitude: $longitude }
          first: $first
          sortKey: DISTANCE
        ) {
          edges {
            node {
              id
              name
              address {
                address1
                address2
                city
                provinceCode
                zip
                countryCode
              }
              metafield(namespace: "amb", key: "public_store_name") {
                value
              }
              emailMetafield: metafield(namespace: "amb", key: "email") {
                value
              }
            }
          }
        }
      }
    `;
      // TEMPORARY FIX: Use actual Shopify store domain when running locally
      // TODO: Remove this after testing is complete
      const hostname = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
        ? 'bakers-delight-live.myshopify.com'
        : window.location.hostname;
      const response = await fetch(`https://${hostname}/api/${apiVersion}/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': accessToken
        },
        body: JSON.stringify({
          query,
          variables: {
            latitude,
            longitude,
            first: 100
          }
        }),
        signal: this.abortController?.signal
      });
      if (!response.ok) {
        throw new Error(`Storefront API failed: ${response.status}`);
      }
      const data = await response.json();
      if (data.errors) {
        console.error('StoreSearch: GraphQL errors', data.errors);
        return { locations: [] };
      }
      const locations = data.data?.locations?.edges?.map((edge) => {
        const node = edge.node;
        const locationId = node.name;
        const name = node.metafield?.value || node.name;
        const email = node.emailMetafield?.value || '';
        return {
          ...node,
          name,
          email
        };
      }) || [];
      return {
        locations
      };
    }
    /**
     * Render search results
     * @param {Array<Object>} locations - Locations to render
     */
    #renderResults(locations) {
      if (!this.resultsPanel) return;
      this.resultsPanel.innerHTML = '';
      this.activeIndex = -1;
      locations.forEach((location, index) => {
        const isSelected = this.selectedLocations.some(loc => loc.id === location.id);
        const inputId = `store-input-${location.id.replace(/\D/g, '')}`;
        const option = document.createElement('div');
        option.className = 'store-search__option';
        option.setAttribute('role', 'option');
        option.setAttribute('data-index', String(index));
        option.setAttribute('data-location-id', String(location.id));
        option.setAttribute('aria-selected', String(isSelected));
        const address = location.address;
        const locationText = [
          location.name,
          address.provinceCode,
          address.zip
        ].filter(Boolean).join(' ');
        // Use radio buttons for single selection, checkboxes for multiple
        const inputType = this.allowMultiple ? 'checkbox' : 'radio';
        const inputName = this.allowMultiple ? `store-${index}` : 'store-selection';
        option.innerHTML = `
        <label class="store-search__option-label ${isSelected ? 'store-search__option-label--selected' : ''}">
          <input 
            type="${inputType}" 
            class="store-search__input"
            id="${inputId}"
            ${isSelected ? 'checked' : ''}
          >
          <span class="store-search__option-text">${this.#escapeHtml(locationText)}</span>
        </label>
      `;
        this.resultsPanel.appendChild(option);
      });
      this.resultsPanel.hidden = false;
    }
    /**
     * Show empty state
     * @param {string} message - Message to display
     */
    #showEmptyState(message) {
      if (!this.resultsPanel) return;
      this.resultsPanel.innerHTML = `
      <div class="store-search__empty">
        ${this.#escapeHtml(message)}
      </div>
    `;
      this.resultsPanel.hidden = false;
    }
    /**
     * Close results panel
     */
    #closeResults() {
      if (!this.resultsPanel) return;
      this.resultsPanel.hidden = true;
      this.activeIndex = -1;
    }
    /**
     * Handle keyboard navigation
     * @param {KeyboardEvent} event
     */
    #handleKeyboard(event) {
      if (!this.resultsPanel || this.resultsPanel.hidden) {
        if (this.isLoading && event.key === 'Enter') {
          event.preventDefault();
        }
        return;
      }
      const options = this.resultsPanel.querySelectorAll('[role="option"]');
      if (options.length === 0) {
        if (event.key === 'Enter') {
          event.preventDefault();
        }
        return;
      }
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.activeIndex = Math.min(this.activeIndex + 1, options.length - 1);
          this.#updateActiveOption(options);
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.activeIndex = Math.max(this.activeIndex - 1, -1);
          this.#updateActiveOption(options);
          break;
        case 'Enter':
          event.preventDefault();
          if (this.activeIndex >= 0) {
            const input = options[this.activeIndex].querySelector('input[type="checkbox"], input[type="radio"]');
            if (input instanceof HTMLInputElement) {
              input.checked = !input.checked;
              input.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
          break;
        case 'Escape':
          event.preventDefault();
          // Cancel any pending requests
          this.abortController?.abort();
          if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = null;
          }
          this.#setLoading(false);
          this.#closeResults();
          break;
      }
    }
    /**
     * Update active option styling
     * @param {NodeListOf<Element>} options - Option elements
     */
    #updateActiveOption(options) {
      options.forEach((option, index) => {
        if (index === this.activeIndex) {
          option.classList.add('store-search__option--active');
          if (option instanceof Element) {
            option.scrollIntoView({ block: 'nearest' });
          }
        } else {
          option.classList.remove('store-search__option--active');
        }
      });
    }
    /**
     * Toggle location selection
     * @param {HTMLElement} option - Option element
     * @param {boolean} checked - Input state
     */
    #toggleLocation(option, checked) {
      const locationId = option.getAttribute('data-location-id');
      const indexStr = option.getAttribute('data-index');
      if (!locationId || !indexStr) return;
      const index = parseInt(indexStr);
      const location = this.locations[index];
      if (!location) return;
      if (checked) {
        // For single selection mode, clear previous selections first
        if (!this.allowMultiple) {
          this.selectedLocations = [];
          // Uncheck all other radio buttons
          const allInputs = this.resultsPanel?.querySelectorAll('input[type="radio"]');
          allInputs?.forEach(input => {
            if (input !== option.querySelector('input')) {
              input.checked = false;
            }
          });
          // Remove selected class from all options
          const allOptions = this.resultsPanel?.querySelectorAll('[role="option"]');
          allOptions?.forEach(opt => {
            opt.setAttribute('aria-selected', 'false');
            const label = opt.querySelector('.store-search__option-label');
            label?.classList.remove('store-search__option-label--selected');
          });
        }
        // Add location if not already selected
        if (!this.selectedLocations.some(loc => loc.id === location.id)) {
          this.selectedLocations.push(location);
          // Add visual selected state
          option.setAttribute('aria-selected', 'true');
          const label = option.querySelector('.store-search__option-label');
          label?.classList.add('store-search__option-label--selected');
          // Dispatch select event
          this.dispatchEvent(new CustomEvent('store:select', {
            detail: {
              locationId,
              location,
              element: option
            },
            bubbles: true
          }));
        }
      } else {
        // Remove location
        this.selectedLocations = this.selectedLocations.filter(loc => loc.id !== location.id);
        // Remove visual selected state
        option.setAttribute('aria-selected', 'false');
        const label = option.querySelector('.store-search__option-label');
        label?.classList.remove('store-search__option-label--selected');
        // Dispatch deselect event
        this.dispatchEvent(new CustomEvent('store:deselect', {
          detail: {
            locationId,
            location,
            element: option
          },
          bubbles: true
        }));
      }
      // Update UI
      this.#updateSelectedLocations();
    }
    /**
     * Update selected locations display
     */
    #updateSelectedLocations() {
      if (!this.chipsContainer) return;
      // Update hidden input
      if (this.hiddenInput) {
        // Check if a custom mapping function is provided
        const mapValueFunctionName = this.getAttribute('map-value-function');
        let mappedValue;
        if (mapValueFunctionName && typeof window[mapValueFunctionName] === 'function') {
          // Use custom mapping function if provided and valid
          try {
            mappedValue = window[mapValueFunctionName](this.selectedLocations);
          } catch (error) {
            console.error('StoreSearch: Error in map-value-function:', error);
            // Fallback to default behavior
            mappedValue = this.selectedLocations.map(loc => loc.name).join(',');
          }
        } else {
          // Default behavior: join location names
          mappedValue = this.selectedLocations.map(loc => loc.name).join(',');
        }
        this.hiddenInput.value = mappedValue;
        // Hide validation message when user selects a location
        if (this.validationMessage && this.selectedLocations.length > 0) {
          this.validationMessage.hidden = true;
          this.validationMessage.style.display = 'none';
          this.validationMessage.textContent = '';
        }
      }
      // Update bakery emails hidden field
      this.#updateBakeryEmails();
      // Show/hide chips container
      this.chipsContainer.hidden = this.selectedLocations.length === 0;
      // Render chips
      this.chipsContainer.innerHTML = this.selectedLocations.map(location => {
        const address = location.address;
        const locationText = [
          location.name,
          address.provinceCode,
          address.zip,
        ].filter(Boolean).join(' ');
        return `
        <button 
          type="button"
          class="store-search__chip" 
          data-location-id="${this.#escapeHtml(location.id)}"
          aria-label="Remove ${this.#escapeHtml(location.name)}"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"/>
          </svg>
          <span class="store-search__chip-text">${this.#escapeHtml(locationText)}</span>
        </button>
      `;
      }).join('');
      // Add remove handlers
      const chips = this.chipsContainer.querySelectorAll('.store-search__chip');
      chips.forEach(chip => {
        chip.addEventListener('click', (event) => {
          const locationId = chip.getAttribute('data-location-id');
          if (locationId) {
            this.#removeLocation(locationId);
          }
        });
      });
    }
    /**
     * Update bakery emails hidden field
     * Finds the hidden field associated with label[data-label="bakery-emails"] and updates it with
     * comma-separated email addresses from selected locations
     */
    #updateBakeryEmails() {
      // Find the label with data-label="bakery-emails"
      const emailLabel = document.querySelector('label[data-label="bakery-emails"]');
      if (!emailLabel) {
        return; // Label doesn't exist, skip update
      }
      // Get the next sibling input element (the hidden field)
      const emailField = emailLabel.nextElementSibling;
      if (!emailField || emailField.tagName !== 'INPUT' || emailField.type !== 'hidden') {
        return; // Hidden input not found or invalid, skip update
      }
    // Extract emails from selected locations and filter out empty values
    const emails = this.selectedLocations
      .map(loc => loc.email)
      .filter(email => email && email.trim() !== '');
    // Update the hidden field with semi-colon separated emails
    // Format: Single: "single@bakery.com"
    // Format: Multiple: "email1@bakery.com; email2@bakery.com"
    emailField.value = emails.join('; ');
  }
    /**
     * Remove a location by ID
     * @param {string} locationId
     */
    #removeLocation(locationId) {
      const location = this.selectedLocations.find(loc => loc.id === locationId);
      if (!location) return;
      this.selectedLocations = this.selectedLocations.filter(loc => loc.id !== locationId);
      this.#updateSelectedLocations();
      // Update input in results if visible
      if (!this.resultsPanel?.hidden) {
        const option = this.resultsPanel.querySelector(`[data-location-id="${locationId}"]`);
        const input = option?.querySelector('input[type="checkbox"], input[type="radio"]');
        if (input instanceof HTMLInputElement) {
          input.checked = false;
        }
        if (option) {
          option.setAttribute('aria-selected', 'false');
        }
      }
      // Dispatch event
      this.dispatchEvent(new CustomEvent('store:remove', {
        detail: { locationId, location },
        bubbles: true
      }));
    }
    /**
     * Clear all location selections
     */
    #clearAllSelections() {
      this.selectedLocations = [];
      this.#updateSelectedLocations();
      // Update inputs in results if visible
      if (!this.resultsPanel?.hidden) {
        const inputs = this.resultsPanel.querySelectorAll('input[type="checkbox"], input[type="radio"]');
        inputs.forEach(input => {
          if (input instanceof HTMLInputElement) {
            input.checked = false;
          }
        });
        const options = this.resultsPanel.querySelectorAll('[role="option"]');
        options.forEach(option => {
          option.setAttribute('aria-selected', 'false');
        });
      }
      // Dispatch clear event
      this.dispatchEvent(new CustomEvent('store:clear', {
        bubbles: true
      }));
    }
    /**
     * Escape HTML to prevent XSS
     * @param {string} text - Text to escape
     * @returns {string}
     */
    #escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }
    /**
     * Public method to reset the component
     * Clears all data and removes all store-search__chips
     */
    reset() {
      // Clear input value
      if (this.input) {
        this.input.value = '';
      }
      // Hide clear button
      if (this.clearInputButton) {
        this.clearInputButton.hidden = true;
      }
      // Clear all location selections
      this.#clearAllSelections();
      // Reset loading state
      this.#setLoading(false);
      // Close results panel
      this.#closeResults();
      // Clear locations data
      this.locations = [];
      this.selectedLocations = [];
      this.activeIndex = -1;
      // Cancel any pending requests
      this.abortController?.abort();
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = null;
      }
      // Reset loading state
      this.#setLoading(false);
      // Clear any EOI multiselect dropdowns (for forms that use both components)
      this.#clearEOIChips();
    }
    /**
     * Clear EOI multiselect chips if present on the page
     * This is used when store-search is on the same page as EOI multiselect dropdowns
     */
    #clearEOIChips() {
      const section = document.querySelector('.expression-of-interest');
      if (!section) return;
      // Find all EOI multiselect containers
      const containers = section.querySelectorAll('.custom-multiselect.dropdown-initialized');
      containers.forEach(container => {
        const wrapper = container.querySelector('.eoi-multiselect-wrapper');
        if (!wrapper) return;
        const chips = wrapper.querySelector('.eoi-chips');
        const results = wrapper.querySelector('.eoi-results');
        if (!chips || !results) return;
        // Clear all original checkboxes WITHOUT dispatching events
        // to avoid triggering Globo Form Builder validation
        const checkboxes = container.querySelectorAll('.globo-form-input input[type="checkbox"]');
        checkboxes.forEach(cb => {
          cb.checked = false;
        });
        // Clear all result panel checkboxes
        const resultCheckboxes = results.querySelectorAll('.eoi-checkbox');
        resultCheckboxes.forEach(cb => {
          if (cb instanceof HTMLInputElement) {
            cb.checked = false;
          }
        });
        // Update option states
        const options = results.querySelectorAll('.eoi-option');
        options.forEach(option => {
          const label = option.querySelector('.eoi-option-label');
          if (label) {
            label.classList.remove('eoi-option-label--selected');
          }
          option.setAttribute('aria-selected', 'false');
        });
        // Clear chips UI
        chips.innerHTML = '';
        chips.classList.add('hidden');
        // Close dropdown if open
        if (results.classList.contains('open')) {
          results.classList.remove('open');
          const iconContainer = wrapper.querySelector('.eoi-dropdown-icon');
          if (iconContainer) {
            iconContainer.classList.remove('open');
          }
        }
      });
    }
    /**
     * Setup form validation with retry for dynamically loaded forms (e.g., Globo Form Builder)
     */
    #setupFormValidation(retryCount = 0) {
      this.form = this.closest('form');
      if (this.form) {
        // Form found, setup validation
        this.formSubmitHandler = (event) => {
          console.log('StoreSearch: Form submit handler called, selectedLocations:', this.selectedLocations.length);
          if (this.selectedLocations.length === 0) {
            event.preventDefault();
            event.stopPropagation();
            console.log('StoreSearch: Preventing submit, showing validation message');
            // Show validation message
            if (this.validationMessage) {
              const message = this.getAttribute('validation-message') || 'Please select at least one bakery';
              this.validationMessage.textContent = message;
              this.validationMessage.hidden = false;
              this.validationMessage.style.display = 'block';
              console.log('StoreSearch: Validation message set:', message);
            }
            // Scroll to component
            this.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Focus the input
            if (this.input) {
              this.input.focus();
            }
          }
        };
        this.form.addEventListener('submit', this.formSubmitHandler, true);
        console.log('StoreSearch: Form validation setup complete for form:', this.form);
      } else {
        // Form not found yet, retry up to 10 times (5 seconds total)
        if (retryCount < 10) {
          console.log(`StoreSearch: Form not found yet, retrying in 500ms (attempt ${retryCount + 1}/10)`);
          setTimeout(() => {
            this.#setupFormValidation(retryCount + 1);
          }, 500);
        } else {
          console.warn('StoreSearch: Required attribute set but no form found after 10 retries');
        }
      }
    }
    /**
     * Cleanup event listeners
     */
    #cleanup() {
      this.abortController?.abort();
      clearTimeout(this.debounceTimer);
      // Remove form submit listener
      if (this.form && this.formSubmitHandler) {
        this.form.removeEventListener('submit', this.formSubmitHandler, true);
      }
    }
  }
  // Only define the custom element if it hasn't been defined yet
  if (!customElements.get('store-search')) {
    customElements.define('store-search', window.StoreSearch);
  }
} // End of typeof window.StoreSearch === 'undefined' check
